import {
  Component,
  DoCheck,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormGroupDirective, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { InterlayerConfig, LoadWithTranslatedName, PalletInterlayer } from 'src/app/core/types';
import { palletInterlayerToInterlayerConfig } from 'src/app/shared/helpers/pallet-layer-add.helper';
import {
  canonicalInterlayerNameForSave,
  interlayerNameForFormInput,
} from 'src/app/shared/helpers/pallet-interlayer-name.helper';
import { validateZero } from 'src/app/core/validators/validators';

export const INTERLAYER_SELECT_NEW = '__interlayer_new__';

@Component({
  selector: 'gl-load-interlayers',
  templateUrl: './load-interlayers.component.html',
  styleUrls: ['./load-interlayers.component.scss'],
  standalone: false,
})
export class LoadInterlayersComponent implements OnInit, OnDestroy, OnChanges, DoCheck {
  @Input() public disabled = false;
  /** When false, interlayers require an active pallet loading space in the project */
  @Input() public isPallet = true;
  @Input() public savedInterlayerLoads: LoadWithTranslatedName[] = [];
  @Input() public layerQuantity = 1;
  /** Hides template picker and save-to-database when editing from loads list */
  @Input() public listEditMode = false;
  @Input() public initialInterlayer?: PalletInterlayer | null;
  /**
   * When true (subsequent pallet layer group on the cargo space), default dunnage under the layer.
   * When false (first layer group), default interlayer above the layer.
   */
  @Input() public preferUnderFirstLayer = false;

  public readonly selectNewValue = INTERLAYER_SELECT_NEW;
  public form: UntypedFormGroup;
  @ViewChild(FormGroupDirective, { static: false })
  private formDirective?: FormGroupDirective;
  private subscriptions: Subscription[] = [];
  private lastEditInterlayerSignature = '';

  constructor(private translate: TranslateService) {
    this.form = this.buildForm();
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      this.form.controls.selectedId.valueChanges.subscribe((id: string | number) => {
        if (id === INTERLAYER_SELECT_NEW) {
          this.form.controls.name.setValue('', { emitEvent: false });
          this.form.controls.thickness.setValue(null, { emitEvent: false });
          return;
        }
        const load = this.savedInterlayerLoads.find((l) => String(l.id) === String(id));
        if (load) {
          this.form.controls.name.setValue(this.interlayerNameForInput(load.name), { emitEvent: false });
          this.form.controls.thickness.setValue(load.height ?? null, { emitEvent: false });
        }
      }),
    );
    this.applyFrequencyValidators();
    this.syncInitialInterlayerForEditMode();
    this.applyPlacementDefaults();
    this.applyDisabledState();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.layerQuantity && this.form) {
      this.applyFrequencyValidators();
    }
    if (changes.disabled && this.form) {
      this.applyDisabledState();
    }
    if ((changes.initialInterlayer || changes.listEditMode) && this.form) {
      this.syncInitialInterlayerForEditMode();
    }
    if (changes.preferUnderFirstLayer && this.form) {
      this.applyPlacementDefaults();
    }
    if (changes.savedInterlayerLoads && this.form && this.savedInterlayerLoads?.length) {
      const id = this.form.controls.selectedId.value;
      const exists =
        id === INTERLAYER_SELECT_NEW || this.savedInterlayerLoads.some((l) => String(l.id) === String(id));
      if (!exists) {
        this.form.controls.selectedId.setValue(INTERLAYER_SELECT_NEW);
      }
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  public ngDoCheck(): void {
    if (!this.listEditMode) {
      this.lastEditInterlayerSignature = '';
      return;
    }
    const signature = this.getInterlayerSignature(this.initialInterlayer ?? null);
    if (signature !== this.lastEditInterlayerSignature) {
      this.syncInitialInterlayerForEditMode();
      this.lastEditInterlayerSignature = signature;
    }
  }

  public getSelectedTemplateLoad(): LoadWithTranslatedName | undefined {
    const id = this.form.controls.selectedId.value;
    if (id === INTERLAYER_SELECT_NEW) {
      return undefined;
    }
    return this.savedInterlayerLoads.find((l) => String(l.id) === String(id));
  }

  /** Resolved config for API; undefined when tab inactive or form invalid */
  public getResolvedInterlayerConfig(): InterlayerConfig | undefined {
    if (this.listEditMode) {
      return this.resolveInterlayerConfigFromForm();
    }
    if (!this.isPallet || this.disabled) {
      return undefined;
    }
    return this.resolveInterlayerConfigFromForm();
  }

  public shouldRememberSave(): boolean {
    return !!this.form?.controls?.remember?.value;
  }

  public triggerValidation(): void {
    this.formDirective?.onSubmit(undefined);
  }

  public hasValidationErrors(): boolean {
    return this.form.invalid;
  }

  public reset(): void {
    const placement = this.getDefaultPlacement();
    this.form.reset({
      selectedId: INTERLAYER_SELECT_NEW,
      name: '',
      thickness: null,
      frequency: 0,
      placeLastOnTop: placement.placeLastOnTop,
      placeLastOnFirst: placement.placeLastOnFirst,
      remember: false,
    });
    this.applyFrequencyValidators();
    this.applyDisabledState();
  }

  public syncFromInterlayerForListEdit(interlayer: PalletInterlayer | null | undefined): void {
    if (!this.listEditMode) {
      return;
    }
    if (interlayer) {
      this.patchFromInterlayer(interlayer);
      return;
    }
    this.reset();
  }

  public nextSequentialInterlayerName(): string {
    const re = /^interlayer\s*(\d+)$/i;
    let max = 0;
    for (const load of this.savedInterlayerLoads ?? []) {
      const m = re.exec((load.name ?? '').trim());
      if (m) {
        max = Math.max(max, parseInt(m[1], 10));
      }
    }
    const current = (this.form.controls.name.value as string)?.trim();
    const cm = current ? re.exec(current) : null;
    if (cm) {
      max = Math.max(max, parseInt(cm[1], 10));
    }
    return `interlayer ${max + 1}`;
  }

  private buildForm(): UntypedFormGroup {
    return new UntypedFormGroup({
      selectedId: new UntypedFormControl(INTERLAYER_SELECT_NEW),
      name: new UntypedFormControl(''),
      thickness: new UntypedFormControl(null, [Validators.required, validateZero]),
      frequency: new UntypedFormControl(0, [Validators.required]),
      placeLastOnTop: new UntypedFormControl(false),
      placeLastOnFirst: new UntypedFormControl(false),
      remember: new UntypedFormControl(false),
    });
  }

  private getDefaultPlacement(): { placeLastOnTop: boolean; placeLastOnFirst: boolean } {
    if (this.listEditMode) {
      return { placeLastOnTop: false, placeLastOnFirst: false };
    }
    if (this.preferUnderFirstLayer) {
      return { placeLastOnTop: false, placeLastOnFirst: true };
    }
    return { placeLastOnTop: true, placeLastOnFirst: false };
  }

  private applyPlacementDefaults(): void {
    if (this.listEditMode) {
      return;
    }
    const placement = this.getDefaultPlacement();
    this.form.patchValue(
      {
        placeLastOnTop: placement.placeLastOnTop,
        placeLastOnFirst: placement.placeLastOnFirst,
      },
      { emitEvent: false },
    );
  }

  private applyFrequencyValidators(): void {
    const ctrl = this.form.controls.frequency;
    const maxLayers = Math.max(0, (this.layerQuantity || 1) - 1);
    ctrl.setValidators([Validators.required, Validators.min(0), Validators.max(maxLayers)]);
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private applyDisabledState(): void {
    if (this.disabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  private syncInitialInterlayerForEditMode(): void {
    if (!this.listEditMode) {
      return;
    }
    if (this.initialInterlayer) {
      this.patchFromInterlayer(this.initialInterlayer);
      return;
    }
    this.reset();
  }

  private getInterlayerSignature(interlayer: PalletInterlayer | null): string {
    if (!interlayer) {
      return '__none__';
    }
    return [
      interlayer.name ?? '',
      interlayer.dimensions?.height ?? '',
      interlayer.regularity ?? '',
      interlayer.onTop ? 1 : 0,
      interlayer.onBottom ? 1 : 0,
    ].join('|');
  }

  private patchFromInterlayer(interlayer: PalletInterlayer): void {
    const cfg = palletInterlayerToInterlayerConfig(interlayer);
    this.form.patchValue(
      {
        selectedId: INTERLAYER_SELECT_NEW,
        name: this.interlayerNameForInput(cfg.name),
        thickness: cfg.thickness,
        frequency: cfg.everyNLayers,
        placeLastOnTop: cfg.placeLastOnTopLayer,
        placeLastOnFirst: cfg.placeLastOnFirstLayer,
        remember: false,
      },
      { emitEvent: false },
    );
    this.applyFrequencyValidators();
  }

  private resolveInterlayerConfigFromForm(): InterlayerConfig | undefined {
    const thickness = this.parseNumber(this.form.controls.thickness.value);
    const everyNLayers = this.parseIntStrict(this.form.controls.frequency.value);
    if (thickness === null || thickness <= 0 || everyNLayers === null || everyNLayers < 0) {
      return undefined;
    }
    const rawName = (this.form.controls.name.value as string)?.trim();
    const canonicalName = canonicalInterlayerNameForSave(rawName, (key, params) =>
      String(this.translate.instant(key, params)),
    );
    return {
      name: canonicalName || this.nextSequentialInterlayerName(),
      thickness,
      everyNLayers,
      placeLastOnTopLayer: !!this.form.controls.placeLastOnTop.value,
      placeLastOnFirstLayer: !!this.form.controls.placeLastOnFirst.value,
    };
  }

  private parseNumber(v: unknown): number | null {
    if (v === null || v === undefined || v === '') {
      return null;
    }
    if (typeof v === 'number') {
      return Number.isFinite(v) ? v : null;
    }
    if (typeof v === 'string') {
      const n = parseFloat(v.replace(',', '.'));
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }

  private interlayerNameForInput(name: string | undefined | null): string {
    return interlayerNameForFormInput(name, (key, params) =>
      String(this.translate.instant(key, params)),
    );
  }

  private parseIntStrict(v: unknown): number | null {
    if (v === null || v === undefined || v === '') {
      return null;
    }
    if (typeof v === 'number') {
      return Number.isFinite(v) ? Math.floor(v) : null;
    }
    if (typeof v === 'string') {
      const n = parseInt(v, 10);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }
}
